import { nextTick } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useSessionStore } from '@/stores/session-store';
import {
  agentCodeFromQuery,
  consumeReferralLandingPrompt,
  persistAgentCode,
  persistReferralCode,
  referralCodeFromQuery,
} from '@/shared/referral/invite-link';
import { reportInviteVisit } from '@/shared/referral/invite-visit';
import { trackMetaPageView } from '@/shared/tracking/meta-pixel';
import { activeSkinModule, isLorUsSkin, isOroMxSkin } from '@/shared/theme/active-skin';
import type { SkinComponentLoader, SkinPageKey } from '@/shared/theme/skin-module';

// 仅登录后可访问的页面：充值、我的、代理、KYC、资料、稽核、流水、兑换。
// 访客浏览主页/游戏/活动/促销不受限；命中以下路由时浮出登录弹层。
// Lor US 源站游客可进 Me / 充提 / 记录 / Share（动作仍页内弹登录），见 beforeEach 白名单。
const AUTH_REQUIRED_ROUTES = new Set<string>([
  'account',
  'agentShare',
  'audit',
  'deposit',
  'kyc',
  'profile',
  'profileEdit',
  'redeem',
  'rosaDepositRecords',
  'rosaWithdrawHistory',
  'transactionHistory',
  'wallet',
  'withdraw',
]);

/** Lor US 源站游客可浏览（下单/绑手机/切账号仍页内弹登录）。 */
export const LOR_US_GUEST_BROWSABLE_ROUTES = new Set<string>([
  'account',
  'agentShare',
  'deposit',
  'transactionHistory',
  'betRecord',
  'accountRecord',
  'wallet',
  'withdraw',
]);

/** Oro MX 源站游客可浏览。/account（我的）未登录由底栏+路由弹登录，不在此列。 */
export const ORO_MX_GUEST_BROWSABLE_ROUTES = new Set<string>([
  'deposit',
  'withdraw',
  'agentShare',
  'transactionHistory',
  'betRecord',
  'accountRecord',
  'profile',
  'profileEdit',
  'withdrawAccounts',
]);

// 皮肤预览中心需要直接渲染钱包/KYC 等页面进行可视化编辑，不能被登录守卫拦截。
// 预览态仅由初始 iframe URL 决定，且预览中心后续 router.replace 会抹掉该查询参数，
// 因此在模块加载时（首帧、任何导航之前）一次性捕获，避免后续误判。
const IS_SKIN_PREVIEW =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('preview') === 'skin-center';

// Vite 8 对「同一函数内的三元 import()」只挂其中一支 CSS。必须在函数外选定 loader。
// B-lite：注册了 SkinModule 的皮肤由模块提供首页 loader；旧皮肤分支原样保留。
const loadHomePage =
  activeSkinModule?.pages.home ??
  (isOroMxSkin
    ? () => import('@/pages/home/OroMxHomePage.vue')
    : () => import('@/pages/home/HomePage.vue'));

/** B-lite：皮肤模块提供了对应页面 loader 时替换，否则沿用共享页面（旧皮肤永远走 fallback）。 */
const skinPage = (key: SkinPageKey, fallback: SkinComponentLoader): SkinComponentLoader =>
  activeSkinModule?.pages[key] ?? fallback;

const loadGamesPage = skinPage('games', () => import('@/pages/games/GamesPage.vue'));
const loadDepositPage = skinPage('deposit', () => import('@/pages/deposit/DepositPage.vue'));
const loadWithdrawPage = skinPage('withdraw', () => import('@/pages/withdraw/WithdrawPage.vue'));
const loadPromotionsPage = skinPage('promotions', () => import('@/pages/promotions/PromotionsPage.vue'));
const loadBonusPage = skinPage('bonus', () => import('@/pages/bonus/BonusPage.vue'));
const loadAccountPage = skinPage('account', () => import('@/pages/account/AccountPage.vue'));
const loadVipPage = skinPage('vip', () => import('@/pages/vip/VipPage.vue'));
const loadAgentSharePage = skinPage('agentShare', () => import('@/pages/agent/AgentSharePage.vue'));
const loadRecordsPage = skinPage('records', () => import('@/pages/account/TransactionHistoryPage.vue'));
const loadNoticePage = skinPage('notice', () => import('@/pages/account/OroMxNoticePage.vue'));
const loadSignInPage = skinPage('signIn', () => import('@/pages/signin/SignInPage.vue'));
const loadLotteryWheelPage = skinPage('lotteryWheel', () => import('@/pages/lottery/LotteryWheelPage.vue'));
const loadRedeemPage = skinPage('redeem', () => import('@/pages/promotions/RedeemPage.vue'));
const loadGameDetailsPage = skinPage(
  'gameDetails',
  () => import('@/pages/games/OroMxGameDetailsPage.vue'),
);

const routes: RouteRecordRaw[] = [
  {
    component: loadHomePage,
    name: 'home',
    path: '/',
  },
  {
    redirect: '/',
    path: '/home',
  },
  {
    component: loadGamesPage,
    name: 'games',
    path: '/games',
  },
  {
    // Oro MX 源站路径为 /gameDetails/:id。无启动地址时走错误壳，不编造第三方游戏 URL。
    component: loadGameDetailsPage,
    name: 'gameDetails',
    path: '/gameDetails/:id',
  },
  {
    component: loadGamesPage,
    name: 'venue',
    path: '/venue/:type',
  },
  {
    component: loadGamesPage,
    name: 'venueLobby',
    path: '/venue/:type/lobby',
  },
  {
    component: loadDepositPage,
    name: 'deposit',
    path: '/deposit',
  },
  {
    component: loadWithdrawPage,
    name: 'withdraw',
    path: '/withdraw',
  },
  {
    // Oro MX 源站路径为 /team-center，游客可浏览。
    alias: '/team-center',
    component: loadAgentSharePage,
    name: 'agentShare',
    path: '/agent-share',
  },
  {
    component: () => import('@/pages/agent/OroMxTeamHistoryPage.vue'),
    name: 'teamCenterHistory',
    path: '/team-center/history',
  },
  {
    component: () => import('@/pages/agent/OroMxTeamInfoPage.vue'),
    name: 'teamCenterInfo',
    path: '/team-center/info',
  },
  {
    component: () => import('@/pages/agent/OroMxTeamSharePage.vue'),
    name: 'teamCenterShare',
    path: '/team-center/share',
  },
  {
    component: () => import('@/pages/activities/ActivitiesPage.vue'),
    name: 'activities',
    path: '/activities',
  },
  {
    component: () => import('@/pages/activities/ActivityDetailPage.vue'),
    name: 'activityDetail',
    path: '/activities/:activityId',
  },
  {
    component: loadPromotionsPage,
    name: 'promotions',
    path: '/promotions',
  },
  {
    path: '/activity-hall',
    redirect: '/promotions',
  },
  {
    component: () => import('@/pages/activities/OroMxActivityDetailPage.vue'),
    name: 'activityHallDetail',
    path: '/activity-hall/:id',
  },
  {
    component: loadSignInPage,
    name: 'signIn',
    path: '/sign-in',
  },
  {
    component: loadLotteryWheelPage,
    name: 'lotteryWheel',
    path: '/lottery-wheel',
  },
  {
    // Lor US Bonus Tab（源站 pages/activity/activityBonus），游客可浏览。
    // Oro MX 源站路径为 /discount。
    alias: '/discount',
    component: loadBonusPage,
    name: 'bonus',
    path: '/bonus',
  },
  {
    // VIP 中心：游客可看等级表，登录态显示个人进度与可领奖励。
    // Oro MX 源站路径为 /rank-system。
    alias: '/rank-system',
    component: loadVipPage,
    name: 'vip',
    path: '/vip',
  },
  {
    redirect: '/promos/tigrinho-royale',
    path: '/promos',
  },
  {
    component: () => import('@/pages/promotions/PromotionsPage.vue'),
    name: 'promotionDetail',
    path: '/promos/:slug',
  },
  {
    component: loadRedeemPage,
    name: 'redeem',
    path: '/redeem',
  },
  {
    component: loadAccountPage,
    name: 'account',
    path: '/account',
  },
  {
    component: () => import('@/pages/account/AuditPage.vue'),
    name: 'audit',
    path: '/account/audit',
  },
  {
    alias: '/transaction-records',
    component: loadRecordsPage,
    name: 'transactionHistory',
    path: '/account/history',
  },
  {
    component: loadRecordsPage,
    name: 'betRecord',
    path: '/bet-record',
  },
  {
    component: loadRecordsPage,
    name: 'accountRecord',
    path: '/account-record',
  },
  {
    component: loadNoticePage,
    name: 'notice',
    path: '/notice',
  },
  {
    component: () => import('@/pages/account/OroMxRewardDetailsPage.vue'),
    name: 'rewardDetails',
    path: '/reward-details',
  },
  {
    component: () => import('@/pages/account/OroMxSecurityPage.vue'),
    name: 'securitySettings',
    path: '/security-settings',
  },
  {
    component: () => import('@/pages/account/OroMxTutorialPage.vue'),
    name: 'tutorial',
    path: '/tutorial',
  },
  {
    alias: '/account-Information',
    beforeEnter(to) {
      if (!isOroMxSkin) return true;
      return { hash: to.hash, name: 'profileEdit', query: to.query, replace: true };
    },
    component: () => import('@/pages/account/ProfilePage.vue'),
    name: 'profile',
    path: '/account/profile',
  },
  {
    alias: '/account-edit-profile',
    component: () => import('@/pages/account/ProfileEditPage.vue'),
    name: 'profileEdit',
    path: '/account/profile/edit',
  },
  {
    component: () => import('@/pages/account/OroMxWithdrawAccountsPage.vue'),
    name: 'withdrawAccounts',
    path: '/withdraw-accounts',
  },
  {
    component: () => import('@/pages/account/KycPage.vue'),
    name: 'kyc',
    path: '/account/kyc',
  },
  {
    component: () => import('@/pages/account/AccountPage.vue'),
    name: 'wallet',
    path: '/wallet',
  },
  {
    // 登录改为悬浮弹层：该路由不渲染整页，仅作为 beforeEach 守卫的触发锚点。
    // 组件复用主页，避免冷启动直链在守卫重定向到 '/' 之前出现空白闪烁。
    component: loadHomePage,
    name: 'login',
    path: '/login',
  },
  // B-lite：皮肤独有附加路由（旧皮肤为空数组，路由表不变）。
  ...(activeSkinModule?.routes ?? []),
];

/** 共用 `.app-main` 不随 RouterView 卸载，换页必须显式回到顶部。 */
export function resetAppScroll(root: ParentNode = document) {
  const main = root.querySelector('.app-main');
  if (main instanceof HTMLElement) {
    main.scrollTop = 0;
    main.scrollLeft = 0;
  }
  window.scrollTo(0, 0);
}

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
});

if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

router.afterEach((to, from) => {
  void nextTick(() => {
    resetAppScroll();
    if (from.fullPath !== to.fullPath) {
      trackMetaPageView();
    }
  });
});

// 登录/注册改为悬浮弹层：拦截所有指向 login 的导航，原地浮出弹层而非整页跳转。
// 外部直链（如分享链接 /login?ref=CODE）无来源页时，落到主页再浮出弹层。
router.beforeEach((to, from) => {
  const referralCode = referralCodeFromQuery(to.query as Record<string, unknown>);
  if (referralCode) persistReferralCode(referralCode);
  const agentCode = agentCodeFromQuery(to.query as Record<string, unknown>);
  if (agentCode) persistAgentCode(agentCode);
  if (!IS_SKIN_PREVIEW) {
    if (agentCode) reportInviteVisit('AGENT', agentCode, to.path);
    else if (referralCode) reportInviteVisit('PLAYER', referralCode, to.path);
  }

  if (to.name === 'login') {
    const mode = referralCode || agentCode || to.query.mode === 'register' ? 'register' : 'login';
    useAuthModalStore().openAuth({ agentCode, mode, referralCode });

    // 应用内导航：停留在当前页，仅浮出弹层；直链冷启动无来源页则先到主页。
    return from.matched.length > 0 ? false : { path: '/' };
  }

  if ((referralCode || agentCode) && !IS_SKIN_PREVIEW) {
    const session = useSessionStore();
    if (!session.token) session.restoreSession();
    if (!session.isAuthenticated && consumeReferralLandingPrompt()) {
      useAuthModalStore().openAuth({ agentCode, mode: 'register', referralCode });
    }
  }

  // 登录态拦截：未登录访问受限页面时浮出登录弹层，不渲染目标页。
  if (typeof to.name === 'string' && AUTH_REQUIRED_ROUTES.has(to.name) && !IS_SKIN_PREVIEW) {
    if (isLorUsSkin && LOR_US_GUEST_BROWSABLE_ROUTES.has(to.name)) return true;
    if (isOroMxSkin && ORO_MX_GUEST_BROWSABLE_ROUTES.has(to.name)) return true;
    if (activeSkinModule?.guestBrowsableRoutes.has(to.name)) return true;

    const session = useSessionStore();
    // 冷启动直链时 AppShell 尚未恢复会话，这里先按本地令牌补一次，避免误判未登录。
    if (!session.token) session.restoreSession();

    if (!session.isAuthenticated) {
      useAuthModalStore().openAuth({ mode: 'login', pendingPath: to.fullPath });
      return from.matched.length > 0 ? false : { path: '/' };
    }
  }

  return true;
});
